import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createChildConversation,
  createConversation,
  deleteConversation,
  getChildConversations,
  getConversationById,
  getConversationsByProject,
  updateConversation,
} from '../services/conversationService';
import type { CreateConversationInput, UpdateConversationInput } from '@consentropy/shared';
import type { CreateChildConversationInput } from '../services/conversationService';

export function useConversations(projectId: string) {
  return useQuery({
    queryKey: ['conversations', projectId],
    queryFn: () => getConversationsByProject(projectId),
    enabled: !!projectId,
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: ['conversation', id],
    queryFn: () => getConversationById(id),
    enabled: !!id,
  });
}

export function useChildConversations(parentConversationId: string) {
  return useQuery({
    queryKey: ['conversations', 'children', parentConversationId],
    queryFn: () => getChildConversations(parentConversationId),
    enabled: !!parentConversationId,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConversationInput) => createConversation(input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations', input.projectId] });
    },
  });
}

export function useCreateChildConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChildConversationInput) => createChildConversation(input),
    onSuccess: ({ conversation }, input) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations', input.projectId] });
      void queryClient.invalidateQueries({
        queryKey: ['conversations', 'children', input.childOfConversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['transcript', conversation.id] });
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateConversationInput }) =>
      updateConversation(id, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['conversation', id] });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; projectId: string }) => deleteConversation(id),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations', projectId] });
    },
  });
}
